// Co-editing a deck (plans/SLIDES_ARTEFACT.md "Editor (web)", AB#183): who is where, and which
// slides an AI holds. Pure: no React, no Yjs — SlidesEditor feeds it the awareness states and the
// room's live revisions.
//
// - Presence rides the room's awareness, the channel that carries the prose editor's named cursors
//   and the grid's focused cell: each editor publishes `slideSel` = the slide it is on and the
//   elements it has selected. Others outline those elements with the member's name and put the
//   member's avatar on that slide's thumbnail.
// - Element locks (AB#205) ride the same field: an element another EDITOR has selected is locked
//   for this member (no selection, no edit) until they let it go — client-enforced between
//   members, no Y data, as PowerPoint's "X is editing this shape"; the server reads the same field
//   to make other members' agents skip it (AB#300, backend/domain/slide-locks.ts). A viewer's selection
//   (`ro`) is shown but locks nothing; an idle member (2 min without input in the deck) publishes
//   no ids. A hidden tab is not idle (AB#292): it keeps its lock, as the Desktop shell always did.
//   Two members racing for one element: the lower awareness clientID keeps it.
// - A claim is a `working` Revision in the room's Y.Map('revisions') (docs/spec/30-coediting.md
//   "Slide claims"): `slide` set = one slide (read_slide), no `slide` and no § = the whole part
//   (insert/delete/move slides). A claimed slide is read-only in the editor until the claim ends,
//   except the elements this member held when it began (AB#300, `keptUnderClaim`): the server
//   makes other members' agents skip them, so the member goes on editing them. That is UX: the
//   server's three-way write_slide is what keeps a racing member edit.
import type { Revision } from '../lib/types'
import type { Annotation } from './render'

/** The awareness field each slides editor publishes (the grid's is `cell`). */
export const PRESENCE_FIELD = 'slideSel'

export interface SlideSelection {
  slide: string
  ids: string[]
  /** A read-only member: the selection is presence only, it never locks anything. */
  ro?: boolean
}

/** A selection stops locking after this long without input in the deck (AB#205). */
export const IDLE_MS = 2 * 60 * 1000

/**
 * What this member publishes as `slideSel`: null with no slide open; no ids while idle (the local
 * selection stays, it is re-published on the next input); `ro` for a viewer.
 */
export function presenceOf(slide: string | null, ids: readonly string[], opts: { readOnly?: boolean; idle?: boolean } = {}): SlideSelection | null {
  if (!slide) return null
  return { slide, ids: opts.idle ? [] : [...ids], ...(opts.readOnly ? { ro: true } : {}) }
}

export interface RemoteMember {
  clientId: number
  name: string
  color: string
  /** The slide the member is on (null: none open yet). */
  slide: string | null
  /** The elements the member has selected on that slide. */
  ids: string[]
  /** A read-only member: shown, never locking. */
  ro: boolean
}

const FALLBACK_COLOR = '#6b7280'

/** Everyone else in the room (never this connection), with where they are in the deck. */
export function remoteMembers(states: ReadonlyMap<number, unknown>, self: number): RemoteMember[] {
  const out: RemoteMember[] = []
  states.forEach((state, clientId) => {
    if (clientId === self) return
    const s = state as { user?: { name?: unknown; color?: unknown }; [PRESENCE_FIELD]?: unknown }
    if (!s.user || typeof s.user.name !== 'string') return
    const sel = s[PRESENCE_FIELD] as Partial<SlideSelection> | null | undefined
    const slide = sel && typeof sel.slide === 'string' ? sel.slide : null
    const ids = slide && Array.isArray(sel?.ids) ? sel.ids.filter((id): id is string => typeof id === 'string') : []
    out.push({
      clientId,
      name: s.user.name,
      color: typeof s.user.color === 'string' ? s.user.color : FALLBACK_COLOR,
      slide,
      ids,
      ro: sel?.ro === true,
    })
  })
  return out
}

/** The renderer's outlines on one slide: per element, every member who has it selected. */
export function selectionAnnotations(members: RemoteMember[], slide: string): Record<string, Annotation[]> {
  const out: Record<string, Annotation[]> = {}
  for (const m of members) {
    if (m.slide !== slide) continue
    for (const id of m.ids) (out[id] ??= []).push({ color: m.color, label: m.name })
  }
  return out
}

/**
 * Which elements of one slide are locked for this member, and by whom (AB#205). Each editing
 * member's selection locks:
 * - the elements they selected and everything inside them (a group selected locks its members);
 * - the groups above them (`groupOps`, default on): those groups cannot be selected, so not moved,
 *   resized, rotated or ungrouped, while their other members stay free.
 * Read-only members never lock. Tie-break without clocks: when `self` has an element selected too,
 * the lower awareness clientID keeps it (this member then drops it, or the other one does); among
 * remote members the lower clientID is the one named.
 */
export function lockedBy(
  members: readonly RemoteMember[],
  slide: string,
  elements: Readonly<Record<string, { children?: readonly string[] }>>,
  self?: { clientId: number; slide: string | null; ids: readonly string[] },
  { groupOps = true }: { groupOps?: boolean } = {},
): Map<string, RemoteMember> {
  const parents = new Map<string, string>()
  for (const [id, e] of Object.entries(elements)) for (const c of e.children ?? []) if (c in elements) parents.set(c, id)
  /** An element and everything inside it (a visited guard: concurrent nestings may cycle). */
  const whole = (ids: readonly string[]): Set<string> => {
    const out = new Set<string>()
    const walk = (id: string): void => {
      if (out.has(id) || !(id in elements)) return
      out.add(id)
      for (const c of elements[id]?.children ?? []) walk(c)
    }
    ids.forEach(walk)
    return out
  }
  const above = (ids: Iterable<string>): Set<string> => {
    const out = new Set<string>()
    for (const id of ids) for (let p = parents.get(id); p && !out.has(p); p = parents.get(p)) out.add(p)
    return out
  }
  const mine = self && self.slide === slide ? whole(self.ids) : new Set<string>()
  const locks = new Map<string, RemoteMember>()
  const editors = members.filter((m) => !m.ro && m.slide === slide && m.ids.length).sort((a, b) => a.clientId - b.clientId)
  for (const m of editors) {
    // What both selected stays this member's when its clientID is the lower one.
    const held = [...whole(m.ids)].filter((id) => !(self && mine.has(id) && self.clientId < m.clientId))
    for (const id of held) if (!locks.has(id)) locks.set(id, m)
    if (groupOps) for (const id of above(held)) if (!locks.has(id)) locks.set(id, m)
  }
  return locks
}

/**
 * What this member keeps editing on a slide an AI claims (AB#300): the elements they had selected
 * when the claim began, and everything inside them. That selection is their lock, which the server
 * makes other members' agents skip (`write_slide` reports it as `skipped`), so the claim does not
 * take it from them; the rest of the slide stays read-only until the claim ends. A viewer, an idle
 * member (whose selection locks nothing) and a whole-part claim keep nothing — the caller decides.
 */
export function keptUnderClaim(
  elements: Readonly<Record<string, { children?: readonly string[] }>>,
  ids: readonly string[],
): Set<string> {
  const out = new Set<string>()
  const walk = (id: string): void => {
    if (out.has(id) || !(id in elements)) return
    out.add(id)
    for (const c of elements[id]?.children ?? []) walk(c)
  }
  ids.forEach(walk)
  return out
}

/** Who is on each slide, for the avatars on the thumbnails. */
export function membersBySlide(members: RemoteMember[]): Record<string, RemoteMember[]> {
  const out: Record<string, RemoteMember[]> = {}
  for (const m of members) if (m.slide) (out[m.slide] ??= []).push(m)
  return out
}

/** "Sofia Martin" → "SM"; one word → its first two letters. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return (words[0] as string).slice(0, 2).toUpperCase()
  return `${(words[0] as string)[0]}${(words[words.length - 1] as string)[0]}`.toUpperCase()
}

export interface Claims {
  /** A whole-part claim (structural ops in flight): every slide is read-only. */
  part: Revision | null
  /** Slide id → the claim holding it. */
  slides: Map<string, Revision>
}

/**
 * The live claims of a slides room. `revisions` should already be the LIVE set (working and
 * unexpired — Canvas's `workingLocks`); anything not `working` is ignored here too.
 */
export function claimsOf(revisions: readonly Revision[]): Claims {
  let part: Revision | null = null
  const slides = new Map<string, Revision>()
  for (const r of revisions) {
    if (r.status !== 'working') continue
    if (r.slide) {
      if (!slides.has(r.slide)) slides.set(r.slide, r)
    } else if (!r.section && !r.cells) {
      part ??= r
    }
  }
  return { part, slides }
}

/** The claim that makes this slide read-only, if any: the slide's own, else a whole-part one. */
export function claimOn(claims: Claims, slide: string | null | undefined): Revision | null {
  if (claims.part) return claims.part
  return slide ? (claims.slides.get(slide) ?? null) : null
}
